import { useEffect, useCallback } from 'react';
import EventEmitter from 'eventemitter3';
import type { ToolName } from '@ben/columbia/components/assistant/types';

const aiEventEmitter = new EventEmitter();

export interface BaseData {
  toolName: ToolName;
  componentKey: string;
}

export interface UsageData extends BaseData {
  currentValue: string;
  onChangeValue: (newValue: string) => void;
}

export type DismissData = BaseData;

export interface UseEventEmitterOptions {
  eventName: string;
  handler: (data: UsageData | DismissData) => void;
  dependencies?: unknown[];
}

export interface ToolUsageArguments extends UsageData {
  additionalValue?: unknown;
}

const emitToolUsage = (toolUsageArguments: ToolUsageArguments) => {
  aiEventEmitter.emit('tool.usage', toolUsageArguments);
};

const emitToolDismiss = (toolName: string, componentKey: string) => {
  aiEventEmitter.emit('tool.dismiss', {
    toolName,
    componentKey,
  });
};

const useEventEmitter = ({
  eventName,
  handler,
  dependencies = [],
}: UseEventEmitterOptions) => {
  const memoizedHandler = useCallback(handler, [...dependencies, handler]);

  useEffect(() => {
    aiEventEmitter.on(eventName, memoizedHandler);

    return () => {
      aiEventEmitter.off(eventName, memoizedHandler);
    };
  }, [eventName, memoizedHandler]);
};

export { useEventEmitter, emitToolUsage, emitToolDismiss };
